import React, { useEffect, useRef, useState } from 'react';
import { Popup, Form, Checkbox, Icon, Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { ISortableHeader } from '../../app/interfaces/ISortableTable';
import { StyledInlineButton } from '../CommonStyledComponents';

type ColumnsPickerProps = {
  headers: ISortableHeader[];
  visibleColumnKeys: string[];
  onToggle: (key: string) => void;
  onShowAll: () => void;
  onClose?: () => void; // notify parent when the popup is closed
};

const ColumnsPicker = ({ headers, visibleColumnKeys, onToggle, onShowAll, onClose }: ColumnsPickerProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Close only on outside-click or via the X button
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (contentRef.current?.contains(t)) return;
      setOpen(false);
      onClose?.();
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [open, onClose]);

  const handleToggle = (key: string) => {
    onToggle(key);
    setOpen(true);
  };

  return (
    <Popup
      on="click"
      position="bottom right"
      offset={[0, 8]}
      popper={{ positionFixed: true }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => { setOpen(false); onClose?.(); }}
      closeOnTriggerClick={false}
      closeOnDocumentClick={false} // managed manually above
      trigger={
        <div ref={triggerRef} style={{ display: 'inline-flex' }}>
          <StyledInlineButton size="tiny">
            <Icon name="columns" /> Columns
          </StyledInlineButton>
        </div>
      }
    >
      <Popup.Content>
        {/* Stop bubbling so our document listeners don't see inside clicks */}
        <div
          ref={contentRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <StyledPopupHeader>
            <strong>Visible Columns</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button basic size="mini" onClick={onShowAll}>Show all</Button>
              <Button
                basic
                icon="close"
                size="mini"
                onClick={() => { setOpen(false); onClose?.(); }}
                aria-label="Close column picker"
              />
            </div>
          </StyledPopupHeader>
          <Form>
            <StyledCheckboxGroup>
              {headers.map((h) => {
                const k = String(h.key);
                const checked = visibleColumnKeys.includes(k);
                const disableUncheck = checked && visibleColumnKeys.length === 1;
                return (
                  // Form.Field and Checkbox both carry an index signature in
                  // their prop types, so any handler semantic-ui-react does not
                  // declare outright arrives untyped - hence the annotations.
                  <Form.Field
                    key={k}
                    onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                    onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
                    onTouchStart={(e: React.TouchEvent<HTMLElement>) => e.stopPropagation()}
                  >
                    <Checkbox
                      label={h.text}
                      checked={checked}
                      disabled={disableUncheck}
                      onChange={() => handleToggle(k)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e: React.TouchEvent<HTMLInputElement>) => e.stopPropagation()}
                    />
                  </Form.Field>
                );
              })}
            </StyledCheckboxGroup>
          </Form>
        </div>
      </Popup.Content>
    </Popup>
  );
};

export default ColumnsPicker;

const StyledPopupHeader = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const StyledCheckboxGroup = styled('div')`
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;
`;
